/**
 * intent_queue.ts — ONE buffered-request queue for the player's discrete
 * actions (build-now wave 1, 2026-09-12; the traversal guide's §3.4).
 *
 * Before this the jump buffer (0.15 s), the coyote window and the slide latch
 * were three hand-rolled timers in two modules. This is the one shape they
 * share: a request is stamped with the FIXED tick it was made on, lives for a
 * TTL measured in fixed ticks, is consumed AT MOST ONCE by the avatar's fixed
 * step, and ends as exactly one of consumed / expired / rejected / superseded
 * — with a reason, so a surprise action ("why did my jump become a mantle?")
 * is answerable from the log (`__intents()`).
 *
 * TTLs are FIXED TICKS, never wall-clock milliseconds: a wall-clock window
 * measured from the tap expired before any step saw it on a hitching frame
 * (the slide lesson, combat wave — the probe's 3.5 s frame gaps lost every
 * slide). A tick-counted window survives any frame gap by construction.
 *
 * Pure: no DOM, no engine imports — verify:combat exercises it in node.
 */

export type IntentKind = 'jump' | 'slide' | 'crouch' | 'dash' | 'mantle' | 'parry';
export type IntentOutcome = 'consumed' | 'expired' | 'rejected' | 'superseded';

export interface IntentRequest {
  readonly id: number;
  readonly kind: IntentKind;
  /** The fixed tick the request was made on (atlas.fixedTick at the press). */
  readonly tick: number;
  readonly ttlTicks: number;
  /** A direction carried with the request (a dash's double-tapped key). */
  readonly dirX: number;
  readonly dirZ: number;
}

export interface IntentRecord {
  readonly id: number;
  readonly kind: IntentKind;
  readonly tick: number;
  readonly outcome: IntentOutcome;
  readonly reason: string;
  /** The fixed tick the outcome was decided on. */
  readonly atTick: number;
}

/** Default time-to-live per kind, in fixed ticks (60 Hz): a jump 150 ms (the
 *  buffer the avatar module already had), a slide/crouch/dash 100 ms (a tap
 *  just before its precondition lands still counts), a parry 50 ms (the raise
 *  is sent at once — the request only bridges to the next step), a mantle
 *  150 ms (it rides the jump). */
export const INTENT_TTL_TICKS: Readonly<Record<IntentKind, number>> = {
  jump: 9, slide: 6, crouch: 6, dash: 6, mantle: 9, parry: 3,
};

/** How many outcome records the log keeps (diag + probes). */
export const INTENT_LOG_MAX = 32;

export class IntentQueue {
  private nextId = 1;
  private readonly pending: IntentRequest[] = [];
  /** The last INTENT_LOG_MAX outcomes, oldest first. */
  readonly log: IntentRecord[] = [];

  /** File a request. One LIVE request per kind: a second press of the same
   *  kind supersedes the first (the newest press is the player's intent and
   *  its TTL restarts). Returns the request id. */
  request(kind: IntentKind, tick: number, dirX = 0, dirZ = 0, ttlTicks = INTENT_TTL_TICKS[kind]): number {
    const prev = this.take(kind);
    if (prev) this.record(prev, 'superseded', 'a newer press of the same kind', tick);
    const req: IntentRequest = { id: this.nextId++, kind, tick, ttlTicks, dirX, dirZ };
    this.pending.push(req);
    return req.id;
  }

  /** Drop every request whose TTL has run out (call once per fixed step,
   *  BEFORE consuming — an expired press must never fire late). */
  expire(tick: number): number {
    let n = 0;
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const r = this.pending[i];
      if (tick - r.tick >= r.ttlTicks) {
        this.pending.splice(i, 1);
        this.record(r, 'expired', `ttl ${r.ttlTicks} ticks`, tick);
        n++;
      }
    }
    return n;
  }

  /** The live request of a kind (null when none) — not consumed. */
  peek(kind: IntentKind): IntentRequest | null {
    for (const r of this.pending) if (r.kind === kind) return r;
    return null;
  }

  /** Consume the live request of a kind: removed and logged; null when none. */
  consume(kind: IntentKind, tick: number, reason = 'consumed'): IntentRequest | null {
    const r = this.take(kind);
    if (r) this.record(r, 'consumed', reason, tick);
    return r;
  }

  /** Reject the live request of a kind (a precondition that can never be met
   *  while it lives — a slide with no sprint to slide from). */
  reject(kind: IntentKind, tick: number, reason: string): boolean {
    const r = this.take(kind);
    if (r) this.record(r, 'rejected', reason, tick);
    return r !== null;
  }

  /** Every pending kind (diag). */
  pendingKinds(): IntentKind[] { return this.pending.map((r) => r.kind); }
  get size(): number { return this.pending.length; }

  /** Forget everything (world swap, dispose). Logged as rejected. */
  clear(tick: number, reason = 'cleared'): void {
    while (this.pending.length) this.record(this.pending.pop()!, 'rejected', reason, tick);
  }

  private take(kind: IntentKind): IntentRequest | null {
    for (let i = 0; i < this.pending.length; i++) {
      if (this.pending[i].kind === kind) return this.pending.splice(i, 1)[0];
    }
    return null;
  }

  private record(r: IntentRequest, outcome: IntentOutcome, reason: string, atTick: number): void {
    this.log.push({ id: r.id, kind: r.kind, tick: r.tick, outcome, reason, atTick });
    if (this.log.length > INTENT_LOG_MAX) this.log.splice(0, this.log.length - INTENT_LOG_MAX);
  }
}
